import { trackedMap, trackedSet } from '@ember/reactive/collections';
import type Transition from '@ember/routing/transition';
import Service from '@ember/service';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { Featurescontext } from 'frontend-utils/models';
import type AccountSelectorSubscription from 'lumen/models/account-selector-subscription';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  getFeaturesForRoute,
  getPreferenceGroup,
} from 'lumen/utils/constants/navigation';

type SubscriptionId = string;
type FeatureId = string;
type UserId = string;

export type AccountType = 'brand' | 'business' | 'chain' | 'hotel';

const UHV_PREFERENCES_KEY = 'uhv-preferences';

/**
 * Routes whose subscription preference should NOT be persisted.
 * Must be exact route names — no prefix matching.
 */
const NO_PERSIST_ROUTES = new Set([
  'hotel.settings',
  'hotel.settings.index',
  'hotel.events',
  'hotel.events.index',
  'hotel.events.edit',
]);

type PreferenceEntry = { id: string; routeName: string };

interface UserPreferences {
  /**
   * Maps hotelinfoId → preferenceKey → PreferenceEntry.
   * The preferenceKey is either a route name or a preference group key
   * (see ROUTE_PREFERENCE_GROUP).
   */
  subscriptions: Record<string, Record<string, PreferenceEntry>>;
}

const DEFAULT_USER_PREFERENCES: UserPreferences = {
  subscriptions: {},
};

function shouldPersistRoute(routeName: string): boolean {
  return !!getFeaturesForRoute(routeName) && !NO_PERSIST_ROUTES.has(routeName);
}

export default class NavigationService extends Service {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  @tracked accountSelectorSubscriptions: AccountSelectorSubscription[] = [];
  @tracked subscriptionToFeatures = trackedMap<
    SubscriptionId,
    Set<FeatureId>
  >();

  accountSelectionConfirmed = false;
  accountSelectionRedirectQueryParams: Record<string, unknown> | undefined =
    undefined;
  accountSelectionRedirectTo: string | undefined = undefined;

  loadSubscriptionsTask = task(async (hotelinfoId: string) => {
    return await this.store.query<AccountSelectorSubscription>(
      'account-selector-subscription',
      {
        namespace: APIGATEWAY,
        typeKey: `hotelinfos/${hotelinfoId}/subscriptions`,
      },
    );
  });

  /**
   * Get all unique account types from available subscriptions
   */
  get accountTypes(): AccountType[] {
    return [
      ...new Set(this.accountSelectorSubscriptions.map((sub) => sub.type)),
    ];
  }

  /**
   * Get the current subscription from accountSelectorSubscriptions
   */
  get currentSubscription(): AccountSelectorSubscription | undefined {
    const currentSubscriptionId = this.hotelAndMarketService.hotel?.id;
    if (!currentSubscriptionId) {
      return undefined;
    }
    return this.accountSelectorSubscriptions.find(
      (sub) => sub.id === currentSubscriptionId,
    );
  }

  get hasAnyRestrictedOrDeniedFeatures(): boolean {
    return this.accountSelectorSubscriptions.some(({ id }) => {
      const features = this.getFeatures(id!);

      return [...features].some(
        (feature) =>
          (feature.startsWith('restricted.') &&
            feature !== 'restricted.user.staff.context') ||
          (feature.startsWith('denied.') &&
            feature !== 'denied.user.staff.context'),
      );
    });
  }

  get hasUnifiedHotelView(): boolean {
    const isSingleProperty = Boolean(this.hotelAndMarketService.hotel);
    const hasFeatureToggle =
      this.featureToggle.featureFlagsAvailable &&
      this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW);

    return isSingleProperty && hasFeatureToggle;
  }

  get isSingleSubscription(): boolean {
    return this.accountSelectorSubscriptions.length <= 1;
  }

  private get userId(): UserId | undefined {
    return this.hotelAndMarketService.user?.id ?? undefined;
  }

  private get userPreferences(): UserPreferences {
    const { userId } = this;

    if (!userId) {
      return DEFAULT_USER_PREFERENCES;
    }

    const value = this.localStorage.getItem(UHV_PREFERENCES_KEY);

    if (!value) {
      return DEFAULT_USER_PREFERENCES;
    }

    try {
      const userIdToPreferences = JSON.parse(value) as Record<
        UserId,
        UserPreferences
      >;

      return userIdToPreferences[userId] ?? DEFAULT_USER_PREFERENCES;
    } catch {
      // corrupted data, start fresh
      return DEFAULT_USER_PREFERENCES;
    }
  }

  /**
   * Get features for a specific subscription ID
   * This is used for access control to get the current subscription's features
   */
  getFeatures(subscriptionId: string): Set<string> {
    return this.subscriptionToFeatures.get(subscriptionId) ?? trackedSet();
  }

  getPersistedRoute(routeName: string): string | undefined {
    if (!shouldPersistRoute(routeName)) {
      return undefined;
    }

    const hotelinfoId = this.hotelAndMarketService.hotel?.hotelinfo?.id;

    if (!hotelinfoId) {
      return undefined;
    }

    const prefKey = getPreferenceGroup(routeName)?.key ?? routeName;
    return this.userPreferences.subscriptions[hotelinfoId]?.[prefKey]
      ?.routeName;
  }

  getSubscriptionByType(
    type: AccountType,
  ): AccountSelectorSubscription | undefined {
    return this.accountSelectorSubscriptions.find((sub) => sub.type === type);
  }

  getSubscriptionId(
    featureName: string | string[],
    routeName?: string,
  ): string | undefined {
    // Check persisted preference first
    if (routeName && shouldPersistRoute(routeName)) {
      const hotelinfoId = this.hotelAndMarketService.hotel?.hotelinfo?.id;

      if (hotelinfoId) {
        const prefKey = getPreferenceGroup(routeName)?.key ?? routeName;
        const persistedId =
          this.userPreferences.subscriptions[hotelinfoId]?.[prefKey]?.id;

        if (
          persistedId &&
          this.subscriptionHasFeature(persistedId, featureName)
        ) {
          return persistedId;
        }
      }
    }

    const currentSubscriptionId = this.hotelAndMarketService.hotel?.id;

    // If current subscription has the feature, use it
    if (
      currentSubscriptionId &&
      this.subscriptionHasFeature(currentSubscriptionId, featureName)
    ) {
      return currentSubscriptionId;
    }

    // Otherwise, find the first subscription that has this feature
    for (const [subscriptionId] of this.subscriptionToFeatures) {
      if (this.subscriptionHasFeature(subscriptionId, featureName)) {
        return subscriptionId;
      }
    }

    return undefined;
  }

  getSubscriptionsForRoute(routeName: string): AccountSelectorSubscription[] {
    const featureList = getFeaturesForRoute(routeName);

    if (!featureList) {
      return this.accountSelectorSubscriptions;
    }

    return this.accountSelectorSubscriptions.filter((sub) => {
      const subFeatures = this.subscriptionToFeatures.get(sub.id!);
      return featureList.some((feature) => subFeatures?.has(feature));
    });
  }

  hasDuplicateTypes(subscriptions: AccountSelectorSubscription[]): boolean {
    const uniqueTypes = new Set(subscriptions.map(({ type }) => type));
    return uniqueTypes.size < subscriptions.length;
  }

  hasFeatureInSomeSubscription(featureName: string): boolean {
    for (const features of this.subscriptionToFeatures.values()) {
      if (features.has(featureName)) {
        return true;
      }
    }
    return false;
  }

  async load(hotelinfoId: string): Promise<void> {
    try {
      const requestParams = {
        namespace: APIGATEWAY,
        typeKey: `hotelinfos/${hotelinfoId}/featurescontext`,
      };

      const featurescontexts = await this.store.query<Featurescontext>(
        'featurescontext',
        requestParams,
      );

      this.subscriptionToFeatures.clear();

      for (const { availableFeatures, subscription } of featurescontexts) {
        this.subscriptionToFeatures.set(
          subscription,
          trackedSet(availableFeatures),
        );
      }

      this.accountSelectorSubscriptions =
        await this.loadSubscriptionsTask.perform(hotelinfoId);
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }
  }

  /*
   * Redirects to account selection when navigating from another product.
   * Stores the target route and redirects back after selection.
   * Call in beforeModel and return early if true.
   */
  redirectToAccountSelection(transition: Transition): boolean {
    if (
      !this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW)
    ) {
      return false;
    }

    const routeFrom = transition.from?.name;
    const routeTo = transition.to?.name;

    // Check if the previous subscription belongs to the current hotelinfo.
    const previousSubscriptionId = transition.from?.find(
      ({ name }) => name === 'hotel',
    )?.params?.['id'];
    const isSameHotelinfo =
      !previousSubscriptionId ||
      this.accountSelectorSubscriptions.some(
        ({ id }) => id === previousSubscriptionId,
      );

    // Allow the full transition chain after account-selection confirmation.
    // This covers the direct transition, intermediate redirects (e.g.
    // parent index → child via replaceWith), and afterModel child
    // redirects (e.g. myotb → myotb.active).
    if (this.accountSelectionConfirmed && this.accountSelectionRedirectTo) {
      const target = this.accountSelectionRedirectTo;
      const isTargetOrDescendant =
        routeTo === target || routeTo?.startsWith(`${target}.`);

      if (isTargetOrDescendant) {
        if (routeFrom !== 'hotel.account-selection') {
          this.accountSelectionConfirmed = false;
          this.accountSelectionRedirectTo = undefined;
          this.accountSelectionRedirectQueryParams = undefined;
        }

        return false;
      }
    }

    if (routeFrom === 'hotel.account-selection') {
      this.accountSelectionRedirectTo = undefined;
      this.accountSelectionRedirectQueryParams = undefined;
    } else {
      this.accountSelectionConfirmed = false;
      this.accountSelectionRedirectTo = undefined;
      this.accountSelectionRedirectQueryParams = undefined;
    }

    if (routeTo === undefined) {
      return false;
    }

    // Don't redirect when staying on the same route and hotel — the inline
    // account selector already handled the subscription switch.
    if (isSameHotelinfo && routeTo === routeFrom) {
      return false;
    }

    // Skip account selection if there's only one subscription for the target route
    if (this.getSubscriptionsForRoute(routeTo).length <= 1) {
      return false;
    }

    const parentRoute = routeTo.slice(0, routeTo.lastIndexOf('.'));
    const redirectRoute = getFeaturesForRoute(parentRoute)
      ? parentRoute
      : routeTo;

    this.accountSelectionRedirectTo = redirectRoute;
    this.accountSelectionRedirectQueryParams = transition.to?.queryParams;
    transition.abort();

    void this.router.transitionTo(
      'hotel.account-selection',
      this.hotelAndMarketService.hotelOrMarket,
      {
        queryParams: {
          redirectTo: redirectRoute,
        },
      },
    );

    return true;
  }

  saveSubscription(routeName: string, subscriptionId: string): void {
    if (!shouldPersistRoute(routeName)) {
      return;
    }

    const hotelinfoId = this.hotelAndMarketService.hotel?.hotelinfo?.id;

    if (!hotelinfoId) {
      return;
    }

    const group = getPreferenceGroup(routeName);
    const prefKey = group?.key ?? routeName;

    // For grouped routes, resolve the landing route. First check which
    // candidate the current route belongs to (e.g. hotel.pricing-manager.index
    // → hotel.pricing-manager). Fall back to feature-based resolution for
    // subscriptions that don't match the current route's product.
    let resolvedRoute = routeName;

    if (group) {
      const currentCandidate = group.candidates.find(
        (candidate) =>
          routeName === candidate.routeName &&
          this.subscriptionHasFeature(subscriptionId, candidate.featureName),
      );

      const match =
        currentCandidate ??
        group.candidates.find((candidate) =>
          this.subscriptionHasFeature(subscriptionId, candidate.featureName),
        );

      if (match) {
        resolvedRoute = match.routeName;
      }
    }

    const { subscriptions } = this.userPreferences;

    const newSubscriptions: Record<string, Record<string, PreferenceEntry>> = {
      ...subscriptions,
      [hotelinfoId]: {
        ...subscriptions[hotelinfoId],
        [prefKey]: { id: subscriptionId, routeName: resolvedRoute },
      },
    };

    this.updateLocalStorage({
      subscriptions: newSubscriptions,
    });
  }

  private subscriptionHasFeature(
    subscriptionId: string,
    featureName: string | string[],
  ): boolean {
    const features = this.subscriptionToFeatures.get(subscriptionId);

    if (!features) {
      return false;
    }

    if (Array.isArray(featureName)) {
      return featureName.some((f) => features.has(f));
    }

    return features.has(featureName);
  }

  private updateLocalStorage(preferences: UserPreferences): void {
    const { userId } = this;

    if (!userId) {
      return;
    }

    let userIdToPreferences: Record<UserId, UserPreferences> = {};
    const value = this.localStorage.getItem(UHV_PREFERENCES_KEY);

    if (value) {
      try {
        const parsed = JSON.parse(value) as unknown;

        if (
          parsed !== null &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed)
        ) {
          userIdToPreferences = parsed as Record<UserId, UserPreferences>;
        }
      } catch {
        // corrupted data, start fresh
      }
    }

    userIdToPreferences[userId] = preferences;

    this.localStorage.setItem(
      UHV_PREFERENCES_KEY,
      JSON.stringify(userIdToPreferences),
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    navigation: NavigationService;
  }
}
